import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { RoleGuard } from "@/components/RoleGuard";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollHorizontal } from "@/components/ScrollHorizontal";
import { FaixaKpis, PageHeader, SecaoCard, SegmentedControl } from "@/components/padroes";
import type { Serie } from "@/components/dashboard/GraficoColunas";
import { MiniGraficoColunas } from "@/components/dashboard/MiniGraficoColunas";
import { api } from "@/lib/api";
import { formatCents, formatMesRef } from "@/lib/format";
import { formatDataCivil } from "@/data/beneficios";
import { mesCurto, porcento, type Headcount } from "@/data/dashboard";
import { toast } from "sonner";
import { AlertTriangle, BarChart3, Loader2, PartyPopper, Table2 } from "lucide-react";

const PJ_CLT: Serie[] = [
  { chave: "pj", rotulo: "PJ", cor: "var(--viz-1)" },
  { chave: "clt", rotulo: "CLT", cor: "var(--viz-2)" },
];

const inteiro = (v: number) => v.toLocaleString("pt-BR");

const tempo = (meses: number | null) =>
  meses === null ? "—" : meses < 12 ? `${inteiro(Math.round(meses))} meses` : `${(meses / 12).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} anos`;

type Linha = Record<string, string | number | null>;

/**
 * Card de mini-gráfico: título e legenda no cabeçalho, colunas no corpo e um
 * botão para ver os mesmos números em tabela.
 */
const CardGrafico = ({
  titulo,
  descricao,
  dados,
  chaveX,
  rotuloX,
  series,
  formatarX = (v) => v,
  empilhado = true,
  largura,
}: {
  titulo: string;
  descricao?: ReactNode;
  dados: Linha[];
  chaveX: string;
  rotuloX: string;
  series: Serie[];
  formatarX?: (v: string) => string;
  empilhado?: boolean;
  largura?: number;
}) => {
  const [tabela, setTabela] = useState(false);
  const total = (l: Linha) => series.reduce((s, x) => s + Number(l[x.chave] ?? 0), 0);
  const comTotal = empilhado && series.length > 1;
  return (
    <SecaoCard
      titulo={titulo}
      descricao={descricao}
      acoes={
        <>
          {series.length > 1 && (
            <ul className="flex gap-3 text-xs text-muted-foreground" aria-label="Legenda">
              {series.map((s) => (
                <li key={s.chave} className="flex items-center gap-1.5">
                  <span className="h-[9px] w-[9px] rounded-[3px]" style={{ background: s.cor }} aria-hidden />
                  {s.rotulo}
                </li>
              ))}
            </ul>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground"
            onClick={() => setTabela((t) => !t)}
            aria-pressed={tabela}
            title={tabela ? "Ver gráfico" : "Ver tabela"}
            aria-label={tabela ? "Ver gráfico" : "Ver tabela"}
          >
            {tabela ? <BarChart3 className="!size-3.5" /> : <Table2 className="!size-3.5" />}
          </Button>
        </>
      }
    >
      {tabela ? (
        <div className="max-h-[204px] overflow-auto rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{rotuloX}</TableHead>
                {series.map((s) => (
                  <TableHead key={s.chave} className="text-right">
                    {s.rotulo}
                  </TableHead>
                ))}
                {comTotal && <TableHead className="text-right">Total</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {dados.map((l) => (
                <TableRow key={String(l[chaveX])}>
                  <TableCell className="py-2">{formatarX(String(l[chaveX]))}</TableCell>
                  {series.map((s) => (
                    <TableCell key={s.chave} className="py-2 text-right">
                      {inteiro(Number(l[s.chave] ?? 0))}
                    </TableCell>
                  ))}
                  {comTotal && <TableCell className="py-2 text-right font-semibold">{inteiro(total(l))}</TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <MiniGraficoColunas
          dados={dados}
          chaveX={chaveX}
          series={series}
          formatarValor={inteiro}
          formatarX={formatarX}
          empilhado={empilhado}
          largura={largura}
        />
      )}
    </SecaoCard>
  );
};

const RHHeadcount = () => {
  const [dados, setDados] = useState<Headcount | null>(null);
  const [distribuicao, setDistribuicao] = useState<"tomador" | "departamento">("tomador");

  useEffect(() => {
    api
      .get<Headcount>("/dashboard/headcount")
      .then(setDados)
      .catch((err) => toast.error(err instanceof Error ? err.message : "Erro ao carregar o headcount"));
  }, []);

  if (!dados) {
    return (
      <RoleGuard role="rh">
        <AppLayout title="Headcount">
          <div className="flex justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </AppLayout>
      </RoleGuard>
    );
  }

  const { resumo: r } = dados;
  const naFolha = dados.movimentacao.filter((m) => m.naFolha !== null);
  const ultimaFolha = naFolha[naFolha.length - 1];
  const grupos = distribuicao === "tomador" ? dados.porTomador : dados.porDepartamento;
  const pctPj = r.total ? (r.pj / r.total) * 100 : 0;

  return (
    <RoleGuard role="rh">
      <AppLayout title="Headcount">
        <div className="mx-auto flex max-w-7xl flex-col gap-5 tabular-nums">
          <PageHeader
            titulo="Headcount"
            subtitulo={`Quem está na empresa hoje, ${formatDataCivil(dados.hoje)}: PJ ativos com salário e CLT com contrato não desligado.`}
          />

          {/* Topo: pessoas ativas + 8 indicadores */}
          <section className="flex flex-wrap overflow-hidden rounded-xl border bg-card">
            <div className="flex flex-[1_1_260px] flex-col gap-1.5 border-r border-border-soft px-6 py-[22px]">
              <span className="text-sm text-muted-foreground">Pessoas ativas</span>
              <span className="text-[44px] font-semibold leading-none tracking-heroi">{inteiro(r.total)}</span>
              <div className="mt-2 flex h-2 gap-0.5 overflow-hidden rounded-full bg-muted" aria-hidden>
                {r.pj > 0 && <div style={{ width: `${pctPj}%`, background: "var(--viz-1)" }} />}
                {r.clt > 0 && <div style={{ width: `${100 - pctPj}%`, background: "var(--viz-2)" }} />}
              </div>
              <div className="flex gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <span className="h-[9px] w-[9px] rounded-[3px]" style={{ background: "var(--viz-1)" }} />
                  {inteiro(r.pj)} PJ
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="h-[9px] w-[9px] rounded-[3px]" style={{ background: "var(--viz-2)" }} />
                  {inteiro(r.clt)} CLT
                </span>
              </div>
            </div>
            <FaixaKpis
              embutida
              className="flex-[3_1_560px] border-t-0"
              itens={[
                { rotulo: "Em experiência", valor: inteiro(r.emExperiencia), apoio: `até ${r.diasExperiencia} dias de casa` },
                { rotulo: "Admissões no mês", valor: inteiro(r.admissoesMes) },
                { rotulo: "Desligamentos no mês", valor: inteiro(r.desligamentosMes) },
                {
                  rotulo: "Turnover (12 meses)",
                  valor: r.turnover12m === null ? "—" : porcento(r.turnover12m),
                  apoio: "(admissões + desligamentos) ÷ 2 ÷ headcount médio",
                },
                { rotulo: "Salário médio", valor: formatCents(r.salarioMedio), apoio: "PJ: fixo + complemento" },
                { rotulo: "Salário mediano", valor: formatCents(r.salarioMediano) },
                {
                  rotulo: "Cadastros incompletos",
                  valor: inteiro(r.comPendencia),
                  tom: r.comPendencia > 0 ? "alerta" : undefined,
                  apoio:
                    r.comPendencia > 0 ? (
                      <a href="#pendencias" className="text-primary-text hover:underline">
                        ver lista abaixo
                      </a>
                    ) : (
                      "tudo em dia"
                    ),
                },
                {
                  rotulo: "Na última folha",
                  valor: ultimaFolha ? inteiro(ultimaFolha.naFolha!) : "—",
                  apoio: ultimaFolha ? formatMesRef(ultimaFolha.competencia) : "sem folha",
                },
              ]}
            />
          </section>

          <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 460px), 1fr))" }}>
            <CardGrafico
              titulo="Admissões e desligamentos — 12 meses"
              descricao="Desligamento do CLT pela data do contrato; do PJ, pela data em que o cadastro foi inativado."
              dados={dados.movimentacao}
              chaveX="competencia"
              rotuloX="Mês"
              formatarX={mesCurto}
              empilhado={false}
              largura={10}
              series={[
                { chave: "admissoes", rotulo: "Admissões", cor: "var(--viz-3)" },
                { chave: "desligamentos", rotulo: "Desligamentos", cor: "var(--viz-2)" },
              ]}
            />
            <CardGrafico
              titulo="Pessoas pagas na folha — 12 meses"
              descricao="PJ e CLT que entraram na folha de cada competência."
              dados={dados.movimentacao.map((m) => ({ competencia: m.competencia, naFolha: m.naFolha ?? 0 }))}
              chaveX="competencia"
              rotuloX="Mês"
              formatarX={mesCurto}
              largura={18}
              series={[{ chave: "naFolha", rotulo: "Pessoas na folha", cor: "var(--viz-1)" }]}
            />
            <CardGrafico
              titulo="Tempo de casa"
              dados={dados.tempoDeCasa}
              chaveX="faixa"
              rotuloX="Tempo de casa"
              series={PJ_CLT}
            />
            <CardGrafico
              titulo="Faixa salarial"
              descricao="PJ: fixo + complemento. CLT: salário base."
              dados={dados.faixasSalariais}
              chaveX="faixa"
              rotuloX="Faixa"
              series={PJ_CLT}
            />
          </div>

          <SecaoCard
            titulo="Distribuição"
            semPadding
            acoes={
              <SegmentedControl<"tomador" | "departamento">
                aria-label="Distribuição por"
                value={distribuicao}
                onChange={setDistribuicao}
                options={[
                  { value: "tomador", label: "Por tomador" },
                  { value: "departamento", label: "Por departamento" },
                ]}
              />
            }
          >
            <ScrollHorizontal>
              <Table className="min-w-[900px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="pl-5">{distribuicao === "tomador" ? "Tomador" : "Departamento"}</TableHead>
                    <TableHead className="text-right">Pessoas</TableHead>
                    <TableHead className="text-right">PJ</TableHead>
                    <TableHead className="text-right">CLT</TableHead>
                    <TableHead className="text-right">% do total</TableHead>
                    <TableHead className="text-right">Salário médio</TableHead>
                    <TableHead className="text-right">Salário mediano</TableHead>
                    <TableHead className="text-right">Tempo médio</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {grupos.map((g) => (
                    <TableRow key={g.chave}>
                      <TableCell className="pl-5 font-medium">{g.nome}</TableCell>
                      <TableCell className="text-right font-semibold">{g.total}</TableCell>
                      <TableCell className="text-right">{g.pj}</TableCell>
                      <TableCell className="text-right">{g.clt}</TableCell>
                      <TableCell className="text-right text-muted-foreground">{porcento(g.participacao)}</TableCell>
                      <TableCell className="text-right">{g.salarioMedio ? formatCents(g.salarioMedio) : "—"}</TableCell>
                      <TableCell className="text-right">{g.salarioMediano ? formatCents(g.salarioMediano) : "—"}</TableCell>
                      <TableCell className="text-right">{tempo(g.tempoMedioMeses)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollHorizontal>
          </SecaoCard>

          <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 420px), 1fr))" }}>
            <SecaoCard
              titulo={
                <span className="flex items-center gap-2">
                  <PartyPopper className="h-4 w-4 text-primary" />
                  Completam ano de casa este mês
                </span>
              }
            >
              {dados.aniversarios.length === 0 ? (
                <p className="text-sm text-muted-foreground">Ninguém este mês.</p>
              ) : (
                <ul className="flex flex-col text-sm">
                  {dados.aniversarios.map((a) => (
                    <li key={a.id} className="flex items-center justify-between gap-3 border-t border-border-soft py-2">
                      <span className="flex min-w-0 flex-col gap-0.5">
                        <span className="font-medium">{a.nome}</span>
                        <span className="text-xs text-muted-foreground">
                          {a.vinculo.toUpperCase()} · desde {formatDataCivil(a.admissao)}
                        </span>
                      </span>
                      <span className="whitespace-nowrap text-[13px] font-semibold text-primary-text">
                        {a.anos} {a.anos === 1 ? "ano" : "anos"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </SecaoCard>

            <SecaoCard
              className="scroll-mt-20"
              titulo={
                <span id="pendencias" className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  Cadastros incompletos ({dados.pendencias.length})
                </span>
              }
              descricao="Atrapalham a folha, a bonificação e este painel."
            >
              {dados.pendencias.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhum.</p>
              ) : (
                <ul className="flex max-h-[360px] flex-col overflow-auto text-sm">
                  {dados.pendencias.map((p) => (
                    <li key={p.id} className="flex items-center justify-between gap-3 border-t border-border-soft py-2">
                      <span className="flex min-w-0 flex-col gap-0.5">
                        <span className="font-medium">
                          {p.nome}
                          <span className="ml-2 text-[11px] font-normal text-muted-foreground">
                            {p.vinculo.toUpperCase()}
                          </span>
                        </span>
                        <span className="text-xs text-muted-foreground">{p.pendencias.join(" · ")}</span>
                      </span>
                      <Link
                        to="/rh/colaboradores"
                        className="whitespace-nowrap text-[13px] font-medium text-primary-text hover:underline"
                      >
                        Completar
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </SecaoCard>
          </div>
        </div>
      </AppLayout>
    </RoleGuard>
  );
};

export default RHHeadcount;
